/**
 * Kapselkomponente für Messwerteingabe der Kalibrierung eines Opazimeters (Abgastester für Kompressionszünder)
 */

import { observer } from 'mobx-react';
import { computed, action } from 'mobx';
import * as React from 'react';
import { inject } from '../../../utils/decorators';
import { AuftragStore } from '../../../stores/AuftragStore';
import Callout from '../../site/Callout';
import VorgangFormularElement from '../VorgangFormularElement';
import AgtPruefortParameter from './AgtPruefortParameter';
import AgtOpazimeterMessung from './AgtOpazimeterMessung';
import { FormControl, Checkbox } from 'react-bootstrap';
import { round } from '../../../utils/round';
import { bind } from '../../../utils/decorators';
import { IObjekt, AGT_OPAZIMETER, AGT_KOMBI } from '../../../models/ApiTypes';
import { AgtOpazimeterKalibrierungStore } from '../../../stores/AgtOpazimeterKalibrierungStore';
import { ApiStore } from '../../../stores/ApiStore';
import { UiStore } from '../../../stores/UiStore';

export interface IAgtOpazimeterKalibrierungProps {
  elementguid: string;
  kategorieguid: string;
  editable?: boolean;
  value?: string;
  element: VorgangFormularElement;
}

@observer
export default class AgtOpazimeterKalibrierung extends React.Component<IAgtOpazimeterKalibrierungProps, {}> {

    @inject(AuftragStore) auftragStore: AuftragStore;
    @inject(ApiStore) apiStore: ApiStore;
    @inject(UiStore) uiStore: UiStore;
    @inject(AgtOpazimeterKalibrierungStore) kalibrierungStore: AgtOpazimeterKalibrierungStore;

    filterTypen: Array<number>;

    // Für Auswertung relevante Eigenschaften des gewählten Abgastesters
    abgastester: IObjekt | undefined;
    messkammerLaenge: number;
    fehlerGrenzeGeraet: number;

    constructor(props: IAgtOpazimeterKalibrierungProps) {
      super(props);

      // Datenübernahme aus dem im Auftrag gesetzten Opazimeter
      const kalGegenstand = this.kalibrierungStore.getKalibrierGegenstand();
      if (kalGegenstand && (kalGegenstand.art === AGT_OPAZIMETER || kalGegenstand.art === AGT_KOMBI)) {
        this.abgastester = kalGegenstand;
      }

      // Fehlergrenze Koeffizient aus den Meta-Daten
      // Gerät wird bei Anlage über den Koeffizienten klassifiziert
      this.fehlerGrenzeGeraet = 0.1;
      if (this.abgastester) {
        if (this.abgastester && this.abgastester.meta) {
          let metadatum = this.abgastester.meta.find((meta) => meta.bezeichnung === 'agt_fehlergrenze');
          if (metadatum) {
            this.fehlerGrenzeGeraet = round(parseFloat(metadatum.inhalt.replace(',', '.')), 1);
          }
        }
      }

      // Die zu verwendenden Filtertypen ergeben sich aus der Fehlergrenze des Gerätes und sind im Verfahren definiert
      switch (this.fehlerGrenzeGeraet) {
        // Geräte mit Fehlergrenze ±0,1 m^-1
        case 0.1:
          this.filterTypen = [0, 1, 2];
          break;
        // Geräte mit Fehlergrenze ±0,3 m^-1
        case 0.3:
          this.filterTypen = [1, 2, 3];
          break;
        default:
          // Vorgabewerte wenn keine Fehlergrenze zum Objekt hinterlegt wurde
          this.filterTypen = [0, 1, 2];
      }

      // Die Messkammerlänge wird für die Berechnung benötigt und aus den Meta-Daten ermittelt
      this.messkammerLaenge = 430;
      if (this.abgastester) {
        if (this.abgastester.meta) {
          let metadatum = this.abgastester.meta.find((meta) => meta.bezeichnung === 'agt_messkammerlänge');
          if (metadatum) {
            this.messkammerLaenge = parseInt(metadatum.inhalt, 10);
          }
        }
      }

      // Gespeicherte Optionen
      if (this.props.value) {
        var obj = JSON.parse(this.props.value);
        if (obj.optionen) {
          // Eingabe des korrigierten Trübungskoeffizienten
          if (obj.optionen.eingabekorrigierterkoeffizient ) {
            this.kalibrierungStore.setEingabeKorrigierterKoeffizient(obj.optionen.eingabekorrigierterkoeffizient === 'on');
          }
          // Justage durchgeführt
          if (obj.optionen.justage) {
            this.kalibrierungStore.setJustage(obj.optionen.justage === 'on');
          }
          // Messunsicherheit anwenden
          if (obj.optionen.messunsicherheitanwenden) {
            this.kalibrierungStore.setMessunsicherheitAnwenden(obj.optionen.messunsicherheitanwenden === 'on');
          }
        }
      }

      this.kalibrierungStore.setReadOnly(this.props.editable ? !this.props.editable : true);
    }

    async componentDidMount() {
      if (this.abgastester) {
        const objektVorlage = await this.apiStore.objektVorlage(this.abgastester.objektguid);
        if (objektVorlage.ok) {
          this.kalibrierungStore.setObjektVorlage(objektVorlage.json);
        }
      }
    }

    /**
     * JSON-Key Prefix für an Back-End übergebene Daten
     */
    @computed get formdataprefix() {
      return `${this.props.kategorieguid}[${this.props.elementguid}]`;
    }

    @bind @action handleEingabeKorrigierterKoeffizientChange(ev: React.FormEvent<FormControl>) {
      this.kalibrierungStore.setEingabeKorrigierterKoeffizient((ev.target as HTMLInputElement).checked);
    }

    @bind @action handleMessunsicherheitAnwendenChange(ev: React.FormEvent<FormControl>) {
      this.kalibrierungStore.setMessunsicherheitAnwenden((ev.target as HTMLInputElement).checked);
    }

    @bind @action handleJustageChange(ev: React.FormEvent<FormControl>) {
      this.kalibrierungStore.setJustage((ev.target as HTMLInputElement).checked);
    }

    public renderMessReihe(nachJustage: boolean) {
      // Wenn bereits Werte erfasst wurden, in jeweiligen Komponenten übernehmen
      let parameter = undefined;
      let messungen = undefined;
      if (this.props.value) {
        var obj = JSON.parse(this.props.value);
        if (nachJustage) {
          if (obj.nachjustage) {
            messungen = obj.nachjustage.messungen;
          }
        } else {
          messungen = obj.messungen;
        }
        if (obj.parameter) {
          parameter = obj.parameter;
        }
      }
      let prefix = this.formdataprefix;
      if (nachJustage) {
        prefix = prefix + '[nachjustage]';
      }
      return (
        <div>
          <AgtOpazimeterMessung
            filterTyp={this.filterTypen[0]}
            fehlergrenzeGeraet={this.fehlerGrenzeGeraet}
            messkammerlaenge={this.messkammerLaenge}
            formdataprefix={`${prefix}[messungen][0]`}
            value={messungen && messungen[0] ? JSON.stringify(messungen[0]) : undefined}
            nachjustage={nachJustage}
          />
          <AgtOpazimeterMessung
            filterTyp={this.filterTypen[1]}
            fehlergrenzeGeraet={this.fehlerGrenzeGeraet}
            messkammerlaenge={this.messkammerLaenge}
            formdataprefix={`${prefix}[messungen][1]`}
            value={messungen && messungen[1] ? JSON.stringify(messungen[1]) : undefined}
            nachjustage={nachJustage}
          />
          <AgtOpazimeterMessung
            filterTyp={this.filterTypen[2]}
            fehlergrenzeGeraet={this.fehlerGrenzeGeraet}
            messkammerlaenge={this.messkammerLaenge}
            formdataprefix={`${prefix}[messungen][2]`}
            value={messungen && messungen[2] ? JSON.stringify(messungen[2]) : undefined}
            nachjustage={nachJustage}
          />
        </div>
      );
    }

    public render() {
      // Wenn bereits Werte erfasst wurden, in jeweiligen Komponenten übernehmen
      let parameter = undefined;
      let messungen = undefined;
      if (this.props.value) {
        var obj = JSON.parse(this.props.value);
        if (obj.parameter) {
          parameter = obj.parameter;
        }
        if (obj.messungen) {
          messungen = obj.messungen;
        }
      }

      return(
        <div>
          {this.abgastester !== undefined ?
            <div>
              <Callout bsStyle="warning">
                <Checkbox
                      inline={true}
                      id="checkbox_anzeige_korrigierter_koeffizient"
                      name={`${this.formdataprefix}[optionen][eingabekorrigierterkoeffizient]`}
                      disabled={this.kalibrierungStore.readOnly}
                      onChange={!this.kalibrierungStore.readOnly ? this.handleEingabeKorrigierterKoeffizientChange : undefined}
                      defaultChecked={this.kalibrierungStore.eingabeKorrigierterKoeffizient}
                    >
                      Gerät zeigt den korrigierten Trübungskoeffizienten an
                </Checkbox>
                <div className="text-hint-light">
                  <b>Hinweis:</b> Wenn diese Option aktiviert ist, findet keine automatische Korrektur des Trübungskoeffizienten über die Messkammerlänge statt.
                </div>
              </Callout>

              {/* Interne Nutzer können wählen ob die Messunsicherheit auf den Toleranzbereich angewendet werden soll */}
              {this.uiStore.userProfile && this.uiStore.userProfile.portaladmin &&
                <Callout bsStyle="warning">
                  <Checkbox
                      inline={true}
                      id="checkbox_mu_anwenden"
                      name={`${this.formdataprefix}[optionen][messunsicherheitanwenden]`}
                      disabled={this.kalibrierungStore.readOnly}
                      onChange={!this.kalibrierungStore.readOnly ? this.handleMessunsicherheitAnwendenChange : undefined}
                      defaultChecked={this.kalibrierungStore.messunsicherheitAnwenden}
                    >
                      Messunsicherheit für Berechnung Toleranzbereich nutzen
                  </Checkbox>
                  <div className="text-hint-light">
                    <b>Hinweis:</b> Diese Auswahl steht nur internen Nutzern zur Verfügung.
                  </div>
                </Callout>
              }

              <AgtPruefortParameter
                titel="Prüfortparameter zu Beginn der Kalibrierung"
                formdataprefix={`${this.formdataprefix}[parameter][0]`}
                bereichLuftfeuchtigkeit={{min: 0, max: 85}}
                bereichUmgebungsdruck={{min: 860, max: 1060}}
                bereichLufttemperatur={{min: 10, max: 30}}
                editable={this.props.editable}
                value={parameter && parameter[0] ? JSON.stringify(parameter[0]) : undefined}
              />

              {this.renderMessReihe(false)}

              {/* Optionale Eingabe nach Justage (wenn notwendig) */}
              <Callout bsStyle="default">
                <Checkbox
                  inline={true}
                  id="checkbox_justage"
                  name={`${this.formdataprefix}[optionen][justage]`}
                  disabled={this.kalibrierungStore.readOnly}
                  onChange={!this.kalibrierungStore.readOnly ? this.handleJustageChange : undefined}
                  defaultChecked={this.kalibrierungStore.justage}
                >
                  Justage durchgeführt
                </Checkbox>
                <div className="text-hint-light">
                    Zeigt eine zweite Messwerteingabe für die Erfassung der Daten nach Durchführung einer Justage an. Beim Erzeugen des Kalibrierscheins werden diese Messwerte übernommen.
                </div>
              </Callout>

              {this.kalibrierungStore.justage &&
                this.renderMessReihe(true)
              }

              <AgtPruefortParameter
                titel="Prüfortparameter am Ende der Kalibrierung"
                formdataprefix={`${this.formdataprefix}[parameter][1]`}
                bereichLuftfeuchtigkeit={{min: 0, max: 85}}
                bereichUmgebungsdruck={{min: 860, max: 1060}}
                bereichLufttemperatur={{min: 10, max: 30}}
                editable={this.props.editable}
                value={parameter && parameter[1] ? JSON.stringify(parameter[1]) : undefined}
              />

            </div>
            :
            <Callout bsStyle="danger">
              <p>Für die Erstellung der Auswertung muss ein Opazimeter oder Kombigerät ausgewählt werden.</p>
            </Callout>
            }
        </div>
      );
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/agt/AgtOpazimeterKalibrierung.tsx